/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useState, type FunctionComponent } from 'react'

import {
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSkeletonRectangle,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useGetBudgets from '@modules/billing-lib/queries/useGetBudgets'
import { CuiAlert } from '@modules/cui/Alert'
import { DocLink } from '@modules/doclinks'
import Feature from '@modules/utils/feature'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { isFeatureActivated } from '@/store'

import notificationMessages from '../messages'
import FeedbackSnippet from '../../FeedbackSnippet'
import { FeedbackSnippetIds } from '../../FeedbackSnippet/FeedbackSnippetIds'
import useBillingNotificationsNav from '../hooks/useBillingNotificationsNav'

import BudgetsPanel from './components/BudgetsPanel'
import BudgetsTable from './BudgetsTable'
import EmptyState from './EmptyState'
import ActionErrors from './components/ActionErrors'

import type { ActionError, Budget as BudgetType } from './types'

const Budgets: FunctionComponent = () => {
  const [actionErrors, setActionErrors] = useState<ActionError>({})
  const { goToCreateBudget } = useBillingNotificationsNav()
  const organizationId = useOrganizationId()
  const { data: budgets, isFetching, isError } = useGetBudgets(organizationId)
  const hasBudgets = !!budgets?.length

  const isBillingCostAlertsCloudResourceFeatureFlagEnabled = isFeatureActivated(
    Feature.billingCostAlertsCloudResource,
  )

  if (isError) {
    return (
      <BudgetsPanel>
        <CuiAlert size='s' type='error' data-test-id='error-message'>
          <FormattedMessage {...notificationMessages.genericErrorMessage} />
        </CuiAlert>
      </BudgetsPanel>
    )
  }

  if (isFetching) {
    return (
      <BudgetsPanel>
        <EuiSkeletonRectangle
          width='100%'
          data-test-subj='billing.notifications.budgets.table.loading'
        />
      </BudgetsPanel>
    )
  }

  const handleAddBudget = () => goToCreateBudget()

  const handleActionError = (error: ActionError) => {
    setActionErrors({
      ...actionErrors,
      ...error,
    })
  }

  const handleCleanActionError = (budgetId: BudgetType['id']) => {
    if (!actionErrors[budgetId]) {
      return
    }

    const cleanedErrors = { ...actionErrors }
    delete cleanedErrors[budgetId]
    setActionErrors(cleanedErrors)
  }

  const hasActionError = Object.keys(actionErrors).length > 0

  return (
    <BudgetsPanel>
      {hasBudgets ? (
        <Fragment>
          <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
            <EuiText size='s' color='subdued'>
              <FormattedMessage
                id='billing.notifications.budgets.table.description'
                defaultMessage='Get notified when your budget reaches certain thresholds. {learnMore}'
                values={{
                  learnMore: (
                    <DocLink
                      data-test-subj='billing.notifications.budgets.table.description.learn-more'
                      link='billingCreateBudgetEmails'
                      showExternalLinkIcon={true}
                    >
                      <FormattedMessage
                        id='billing.notifications.budgets.table.description.learn-more'
                        defaultMessage='Learn more'
                      />
                    </DocLink>
                  ),
                }}
              />
            </EuiText>
            {isBillingCostAlertsCloudResourceFeatureFlagEnabled && (
              <EuiFlexItem grow={false}>
                <EuiButton color='primary' size='s' onClick={handleAddBudget}>
                  <FormattedMessage
                    id='billing.notifications.budgets.add-budget.button'
                    defaultMessage='Add budget'
                  />
                </EuiButton>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          <EuiSpacer size='m' />
          {hasActionError && (
            <ActionErrors errors={actionErrors} onCleanActionError={handleCleanActionError} />
          )}
          <BudgetsTable
            budgets={budgets}
            onActionError={handleActionError}
            onCleanActionError={handleCleanActionError}
          />
          <FeedbackSnippet
            feedbackSnippetId={FeedbackSnippetIds.billingBudgetsFeedback}
            promptViewMessage={
              <FormattedMessage
                id='feedbackSnippet.budgets.promptMessage'
                defaultMessage='How are budgets working for you?'
              />
            }
          />
        </Fragment>
      ) : (
        <EmptyState />
      )}
    </BudgetsPanel>
  )
}

export default Budgets
